import React, { useState, useRef, useCallback } from 'react';
import { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import { useTheme } from '../../../context/ThemeContext';
import { OperationMode, ImageMetadata } from './utils/types';
import { getCroppedImgWithQuality, compressFullImage, downloadProcessedImage } from './utils/imageProcessing';
import OperationSelector from './components/OperationSelector';
import ImagePreview from './components/ImagePreview';
import ImageControls from './components/ImageControls';
// @ts-ignore
import { Helmet } from 'react-helmet';

const ImageProcessor: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [operationMode, setOperationMode] = useState<OperationMode>('crop');
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  const [scale, setScale] = useState(1);
  const [processedImage, setProcessedImage] = useState<string>('');
  const [processedSize, setProcessedSize] = useState<number>(0);
  const [quality, setQuality] = useState<number>(0.8);
  const [format, setFormat] = useState<string>('jpg');
  const [outputWidth, setOutputWidth] = useState<number | ''>('');
  const [outputHeight, setOutputHeight] = useState<number | ''>('');
  const [lockAspectRatio, setLockAspectRatio] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<ImageMetadata | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      const img = new Image();
      img.onload = () => {
        setMetadata({
          width: img.width,
          height: img.height,
          type: file.type.split('/')[1],
          size: file.size,
          lastModified: file.lastModified,
        });
      };
      img.src = reader.result as string;
      setImgSrc(reader.result as string);
      setProcessedImage(''); // Clear any previous processed image
      // Set format to original file format
      let ext = file.type.split('/')[1]?.toLowerCase();
      if (ext === 'jpeg') setFormat('jpeg');
      else if (ext === 'jpg') setFormat('jpg');
      else if (ext === 'png') setFormat('png');
      else if (ext === 'webp') setFormat('webp');
      else if (ext === 'bmp') setFormat('bmp');
      else if (ext === 'avif') setFormat('avif');
      else setFormat('jpg');
    });
    reader.readAsDataURL(file);
  }, []);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    setCrop({
      unit: '%',
      x: 0,
      y: 0,
      width: 100,
      height: 100
    });
  }, []);

  const handleCropComplete = useCallback((crop: PixelCrop) => {
    setCompletedCrop(crop);
  }, []);

  const handleProcessImage = async () => {
    if (!imgRef.current) return;
    try {
      let result;
      if (!completedCrop) return;
      const displayedWidth = imgRef.current.width;
      const displayedHeight = imgRef.current.height;
      result = await getCroppedImgWithQuality(
        imgRef.current.src,
        completedCrop,
        quality,
        displayedWidth,
        displayedHeight,
        format,
        outputWidth !== '' ? Number(outputWidth) : undefined,
        outputHeight !== '' ? Number(outputHeight) : undefined
      );
      setProcessedImage(result.url);
      setProcessedSize(result.size);
    } catch (e) {
      console.error('Error processing image:', e);
    }
  };

  const handleDownload = () => {
    if (!processedImage) return;
    let ext = format === 'jpg' ? 'jpg' : format;
    if (format === 'jpeg') ext = 'jpeg';
    downloadProcessedImage(processedImage, `processed-image.${ext}`);
  };

  const handleReset = () => {
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    setProcessedImage('');
    setScale(1);
    setAspect(undefined);
    setQuality(0.8);
    setFormat('jpg');
    setOutputWidth('');
    setOutputHeight('');
    setLockAspectRatio(false);
    setMetadata(null);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Image Processor",
    "url": "https://davinqi-tools.com/art/image",
    "applicationCategory": "PhotoApplication",
    "operatingSystem": "All",
    "description": "Crop, resize, compress, and convert images to JPG, PNG, WebP, and more. Fast, privacy-friendly, and free image utility by Davinqi.",
    "browserRequirements": "Requires JavaScript. Works in all modern browsers.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Davinqi"
    }
  };

  return (
    <>
      <Helmet>
        <title>Image Processor | Crop, Resize, Compress, Convert Images Online - Davinqi</title>
        <meta name="description" content="Crop, resize, compress, and convert images to JPG, PNG, WebP, and more. Fast, privacy-friendly, and free image utility by Davinqi." />
        <meta property="og:title" content="Image Processor | Crop, Resize, Compress, Convert Images Online - Davinqi" />
        <meta property="og:description" content="Crop, resize, compress, and convert images to JPG, PNG, WebP, and more. Fast, privacy-friendly, and free image utility by Davinqi." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi-tools.com/art/image" />
        <meta property="og:image" content="https://davinqi.com/og-image-image-processor.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Image Processor | Crop, Resize, Compress, Convert Images Online - Davinqi" />
        <meta name="twitter:description" content="Crop, resize, compress, and convert images to JPG, PNG, WebP, and more. Fast, privacy-friendly, and free image utility by Davinqi." />
        <meta name="twitter:image" content="https://davinqi.com/og-image-image-processor.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <OperationSelector
            operationMode={operationMode}
            onModeChange={setOperationMode}
            onReset={handleReset}
          />
          <div className="mt-6">
            <ImagePreview
              imgSrc={imgSrc}
              processedImage={processedImage}
              processedSize={processedSize}
              operationMode={operationMode}
              metadata={metadata}
              cropProps={imgSrc ? {
                crop,
                setCrop,
                onComplete: handleCropComplete,
                aspect,
                scale,
                imgRef,
                onImageLoad,
                isDarkMode: isDarkMode
              } : undefined}
              imgRef={imgRef}
              onFileSelect={handleFileSelect}
            />
            <div className="mt-6">
              <ImageControls
                operationMode={operationMode}
                imgSrc={imgSrc}
                processedImage={processedImage}
                processedSize={processedSize}
                metadata={metadata}
                onProcess={handleProcessImage}
                onDownload={handleDownload}
                onReset={handleReset}
                cropProps={imgSrc ? {
                  aspect,
                  setAspect,
                  scale,
                  setScale,
                  completedCrop
                } : undefined}
                compressProps={{
                  quality,
                  setQuality,
                  format,
                  setFormat,
                  outputWidth,
                  setOutputWidth,
                  outputHeight,
                  setOutputHeight,
                  lockAspectRatio,
                  setLockAspectRatio
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImageProcessor; 